import {
  SET_LOGS,
  SET_SLAB_STATS,
  SET_PROCESSED_SLAB_STATS,
  SET_PROCESSED_SLAB_STATS_FETCHED,
  SET_COMPOUND_STATS,
  SET_PREFIXES,
  SET_POPULAR_ITEMS,
  SET_POPULAR_ITEMS_STATUS,
  SET_PREFIX_POPULAR_ITEMS_STATUS,
  SET_PREFIX_POPULAR_ITEMS_LAST_FETCHED,
  SET_POPULAR_ITEMS_LAST_FETCHED,
  SET_PREFIX_POPULAR_ITEMS,
  SET_PREFIX_STATS,
  SET_COMPOUND_STATS_STATUS,
  SET_COMPOUND_STATS_LAST_FETCHED,
  SET_PREFIX_STATS_STATUS,
  SET_PREFIX_STATS_LAST_FETCHED,
  SET_KEY_STATS,
  SET_KEY_STATS_SERIES,
  SET_SHOW_ADD_PREFIX,
} from "@/store/mutation-types";
import CacheService from "@/services/CacheService";
import { RequestMethod } from "@/types/request-method";
import { TimeRange, TIME_RANGES } from "@/types/time-range";
import {
  DebugModeState,
  LogsPerServer,
  Prefix,
  PrefixSeriesPayload,
  PopularItems,
  SlabStatsPoint,
  KeyStatsPoint,
} from "@/types/debug-mode";
import { Series, SeriesPoint, SeriesPayload, DataStatus } from "@/types/common";
import { StatType } from "@/types/stats";
import { RootState } from "@/types/root-state";
import { NotificationType, Issue } from "@/types/notification";
import * as Msg from "@/config/notification";
import {
  LIFETIME_MARGIN,
  COMPOUND_STATS_LIFETIME,
  PREFIX_STATS_LIFETIME,
  POPULAR_ITEMS_LIFETIME,
  PREFIX_POPULAR_ITEMS_LIFETIME,
  COMPOUND_METRICS_PROCESSED_STATS,
  emptySeriesArray,
} from "@/config/series";
import { setNotificationError } from "@/utils/notification";
import { combineCompoundAndProcessedStats, combinePrefixAndCompoundStats } from "@/utils/debug-mode";
import { getTimeRange } from "@/utils/conversion";
import {
  generateProcessedStatsState,
  generateChartSeries,
  appendCalculatedMetricsToSeries,
  prependColumnNames,
  trimNullPoints,
} from "@/utils/series";
import { getChartIDsFor, getValuesIdxByMetric } from "@/utils/chart";
import { isAdminUsingProd } from "@/utils/admin";
import { Module, MutationTree, ActionTree, GetterTree } from "vuex";
import Vue from "vue";

const namespaced = true;

const state: DebugModeState = {
  logs: null,
  slabStats: null,
  processedSlabStats: generateProcessedStatsState(StatType.SLAB),
  processedSlabStatsFetched: false,
  compoundStats: generateProcessedStatsState(StatType.COMPOUND),
  compoundStatsStatus: {},
  compoundStatsLastFetched: {},
  popularItems: null,
  popularItemsStatus: DataStatus.UNFETCHED,
  popularItemsLastFetched: 0,
  prefixPopularItemsStatus: {},
  prefixPopularItemsLastFetched: {},
  prefixes: null, // id, name
  // cached prefix stats
  prefixStats: {},
  prefixStatsStatus: {},
  prefixStatsLastFetched: {},
  prefixPopularItems: {}, // [id]: PopularItems
  // cached key stats
  keyStats: {}, // [key_name]: stats[]
  keyStatsSeries: {}, // stats are transformed to match the InfluxDB format
  showAddPrefix: false,
};

const mutations: MutationTree<DebugModeState> = {
  [SET_LOGS]: (state, logs: LogsPerServer) => {
    state.logs = logs;
  },
  [SET_SLAB_STATS]: (state, stats: SlabStatsPoint[]) => {
    state.slabStats = stats;
  },
  [SET_PROCESSED_SLAB_STATS]: (state, payload: SeriesPayload) => {
    state.processedSlabStats[payload.timeRange][payload.chartID] = payload.seriesArray;
  },
  [SET_PROCESSED_SLAB_STATS_FETCHED]: (state, fetched: boolean) => {
    state.processedSlabStatsFetched = fetched;
  },
  [SET_COMPOUND_STATS]: (state, payload: SeriesPayload) => {
    // Initial state with time ranges is generated, so
    // no need to check if a time range property exists
    Vue.set(state.compoundStats[payload.timeRange], payload.chartID, payload.seriesArray);
  },
  [SET_COMPOUND_STATS_STATUS]: (state, payload: { timeRange: TimeRange; status: DataStatus }) => {
    Vue.set(state.compoundStatsStatus, payload.timeRange, payload.status);
  },
  [SET_PREFIX_STATS_STATUS]: (state, payload: { prefixID: number; timeRange: TimeRange; status: DataStatus }) => {
    if (!state.prefixStatsStatus[payload.prefixID]) {
      Vue.set(state.prefixStatsStatus, payload.prefixID, {});
    }

    Vue.set(state.prefixStatsStatus[payload.prefixID], payload.timeRange, payload.status);
  },
  [SET_COMPOUND_STATS_LAST_FETCHED]: (state, payload: { timeRange: TimeRange; timestamp: number }) => {
    Vue.set(state.compoundStatsLastFetched, payload.timeRange, payload.timestamp);
  },
  [SET_PREFIX_STATS_LAST_FETCHED]: (state, payload: { prefixID: number; timeRange: TimeRange; timestamp: number }) => {
    if (!state.prefixStatsLastFetched[payload.prefixID]) {
      Vue.set(state.prefixStatsLastFetched, payload.prefixID, {});
    }

    Vue.set(state.prefixStatsLastFetched[payload.prefixID], payload.timeRange, payload.timestamp);
  },
  [SET_POPULAR_ITEMS]: (state, popular_items: PopularItems) => {
    state.popularItems = popular_items;
  },
  [SET_POPULAR_ITEMS_STATUS]: (state, status: DataStatus) => {
    state.popularItemsStatus = status;
  },
  [SET_POPULAR_ITEMS_LAST_FETCHED]: (state, timestamp: number) => {
    state.popularItemsLastFetched = timestamp;
  },
  [SET_PREFIX_POPULAR_ITEMS_STATUS]: (state, payload: { prefixID: number; status: DataStatus }) => {
    Vue.set(state.prefixPopularItemsStatus, payload.prefixID, payload.status);
  },
  [SET_PREFIX_POPULAR_ITEMS_LAST_FETCHED]: (state, payload: { prefixID: number; timestamp: number }) => {
    Vue.set(state.prefixPopularItemsLastFetched, payload.prefixID, payload.timestamp);
  },
  [SET_PREFIXES]: (state, prefixes: Prefix[]) => {
    state.prefixes = prefixes;
  },
  [SET_PREFIX_STATS]: (state, payload: PrefixSeriesPayload) => {
    // Initial state is not generated, because prefixes are initially unknown.
    // Need to check if a prefix ID property, and time range property exist.
    if (!state.prefixStats[payload.prefixID]) {
      Vue.set(state.prefixStats, payload.prefixID, {});
    }

    if (!state.prefixStats[payload.prefixID][payload.timeRange]) {
      Vue.set(state.prefixStats[payload.prefixID], payload.timeRange, {});
    }

    Vue.set(state.prefixStats[payload.prefixID][payload.timeRange], payload.chartID, payload.seriesArray);
  },
  [SET_PREFIX_POPULAR_ITEMS]: (state, payload: { prefixID: number; popularItems: PopularItems }) => {
    Vue.set(state.prefixPopularItems, payload.prefixID, payload.popularItems);
  },
  [SET_KEY_STATS]: (state, payload: { name: string; stats: KeyStatsPoint[] }) => {
    Vue.set(state.keyStats, payload.name, payload.stats);
  },
  [SET_KEY_STATS_SERIES]: (state, payload: { name: string; series: SeriesPoint[] }) => {
    Vue.set(state.keyStatsSeries, payload.name, payload.series);
  },
  [SET_SHOW_ADD_PREFIX]: (state, show: boolean) => {
    state.showAddPrefix = show;
  },
};

const actions: ActionTree<DebugModeState, RootState> = {
  fetchLogs({ commit, dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/introspection/recent_logs`, RequestMethod.GET)
      .then(response => {
        const logs: LogsPerServer = response.data;
        commit(SET_LOGS, logs);
      })
      .catch(error => {
        commit(SET_LOGS, {}); // prevent indefinite loading
        setNotificationError(dispatch, `${Msg.LOGS_ERROR} (${error})`);
      });
  },
  fetchProcessedSlabStatsByTimeRange({ dispatch }) {
    for (const timeRange of TIME_RANGES) {
      dispatch("fetchProcessedSlabStats", timeRange);
    }
  },
  fetchSlabStats({ commit, dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/introspection/slab_stats`, RequestMethod.GET)
      .then(response => {
        const stats: SlabStatsPoint[] = response.data;

        // TODO Is sorting needed?
        stats.sort(function(a: any, b: any) {
          if (a.timestamp < b.timestamp) return -1;
          if (a.timestamp > b.timestamp) return 1;
          return 0;
        });

        commit(SET_SLAB_STATS, stats);
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.SLAB_STATS_ERROR} (${error})`);
      });
  },
  // TODO handle indefinite loading on null response, catch errors
  fetchProcessedSlabStats({ commit, dispatch, rootState }, timeRange: TimeRange) {
    const { startTime, endTime } = getTimeRange(rootState.timeRange, timeRange);

    const params = {
      startTime,
      endTime,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/introspection/processed_slab_stats`, RequestMethod.GET, params).then(
      response => {
        if (response.data === null) {
          return;
        }

        const seriesArray: Series[] = response.data;

        for (const chartID of getChartIDsFor(StatType.SLAB)) {
          const payload: SeriesPayload = {
            timeRange,
            chartID,
            seriesArray: generateChartSeries(seriesArray, chartID),
          };
          commit(SET_PROCESSED_SLAB_STATS, payload);
        }
        dispatch("setProcessedSlabStatsFetched");
      },
    );
  },
  setProcessedSlabStatsFetched({ commit }) {
    commit(SET_PROCESSED_SLAB_STATS_FETCHED, true);
  },
  fetchCompoundStatsByTimeRange({ dispatch }) {
    for (const timeRange of TIME_RANGES) {
      dispatch("fetchCompoundStats", timeRange);
    }
  },
  async fetchCompoundStats({ commit, dispatch, state, rootState }, timeRange: TimeRange) {
    // If we are already fetching data, wait until fetched
    if (state.compoundStatsStatus[timeRange] === DataStatus.FETCHING) {
      /*
      Polling is needed for compound stats only (and not prefix stats or processed stats) because prefix stats depend on compound stats.
      
      e.g. While fetching LAST_HOUR stats for prefix A, we need to start fetching LAST_HOUR compound stats (if not already fetched).
      Then we start fetching LAST_HOUR stats for prefix B right after A, and see that LAST_HOUR compound stats 
      are currently being fetched, so the prefix B fetch needs to wait. We poll to determine when compound stats are 
      ready, and then continue preparing stats for prefix B.
      */
      const poll = (result: { (): boolean }) => {
        const timeout = Date.now() + 10000; // wait up to 10s

        const checkStatus = function(resolve: (value?: unknown) => void, reject: (value?: unknown) => void) {
          if (result()) {
            resolve(result);
          } else if (Date.now() < timeout) {
            setTimeout(checkStatus, 500, resolve, reject);
          } else {
            reject(new Error("Timed out fetching compound stats"));
          }
        };

        return new Promise(checkStatus);
      };

      return await poll(() => state.compoundStatsStatus[timeRange] === DataStatus.FETCHED);
    }

    const valid = !!(
      state.compoundStatsLastFetched[timeRange] &&
      state.compoundStatsLastFetched[timeRange] + COMPOUND_STATS_LIFETIME - LIFETIME_MARGIN >= Date.now()
    );

    if (state.compoundStatsStatus[timeRange] === DataStatus.FETCHED && valid) {
      // Data already fetched and data valid, or fetching. Do not fetch
      return;
    }

    // Data not fetched or invalid, let's fetch
    commit(SET_COMPOUND_STATS_STATUS, {
      timeRange: timeRange,
      status: DataStatus.FETCHING,
    });
    commit(SET_COMPOUND_STATS_LAST_FETCHED, {
      timeRange: timeRange,
      timestamp: Date.now(),
    });

    const { startTime, endTime } = getTimeRange(rootState.timeRange, timeRange);

    const params = {
      startTime,
      endTime,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/introspection/compound_stats`, RequestMethod.GET, params)
      .then(response => {
        if (response.data === null) {
          dispatch("setEmptyCompoundStatSeries", {
            timeRange: timeRange,
            status: DataStatus.FETCHED,
          });
          return;
        }

        let seriesArray: Series[] = response.data;

        // compound stats should always be an array of one series,
        // map anyway to be more robust
        seriesArray = seriesArray.map(series => {
          trimNullPoints(series.values);

          const appendedSeries = appendCalculatedMetricsToSeries(series);

          // prepend columns names only after appending calculated metrics
          const prependedColumns = prependColumnNames(appendedSeries.columns, "compound_");

          return {
            columns: prependedColumns,
            name: series.name,
            values: appendedSeries.values,
          };
        });

        // return because `fetchPrefixStats()` depends on this promise being fulfilled
        return dispatch("fetchProcessedStatsForCompoundStats", {
          startTime,
          endTime,
        }).then(processedStatsSeries => {
          seriesArray = seriesArray.map(series => {
            series = combineCompoundAndProcessedStats(series, processedStatsSeries);
            return series;
          });

          // commit all stats so combined prefix stats can be calculated
          const allPayload: SeriesPayload = {
            timeRange,
            chartID: "allStats",
            seriesArray: seriesArray,
          };
          commit(SET_COMPOUND_STATS, allPayload);

          // commit stats per chart
          for (const chartID of getChartIDsFor(StatType.COMPOUND)) {
            const payload: SeriesPayload = {
              timeRange,
              chartID,
              seriesArray: generateChartSeries(seriesArray, chartID),
            };
            commit(SET_COMPOUND_STATS, payload);
          }

          commit(SET_COMPOUND_STATS_STATUS, {
            timeRange: timeRange,
            status: DataStatus.FETCHED,
          });
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.COMPOUND_STATS_ERROR} for ${timeRange} (${error})`);

        dispatch("setEmptyCompoundStatSeries", {
          timeRange: timeRange,
          status: DataStatus.FAILED,
        });

        commit(SET_COMPOUND_STATS_LAST_FETCHED, {
          timeRange: timeRange,
          timestamp: 0,
        });
      });
  },
  fetchProcessedStatsForCompoundStats({ rootState }, timeRange: { startTime: Date; endTime: Date }) {
    const params = {
      names: COMPOUND_METRICS_PROCESSED_STATS.join(),
      startTime: timeRange.startTime,
      endTime: timeRange.endTime,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/insight/processed_stats`, RequestMethod.GET, params).then(
      response => {
        if (response.data && response.data[0]) {
          const series: Series = response.data[0];
          trimNullPoints(series.values);
          return series;
        }
        return {
          columns: ["time", ...COMPOUND_METRICS_PROCESSED_STATS],
          name: "",
          values: [],
        };
      },
    );
  },
  fetchPopularItems({ commit, dispatch, rootState }, invalidate?: boolean) {
    let valid = false;
    if (!invalidate) {
      valid = !!(state.popularItemsLastFetched + POPULAR_ITEMS_LIFETIME - LIFETIME_MARGIN >= Date.now());
    }

    if (
      (state.popularItemsStatus === DataStatus.FETCHED && valid) ||
      state.popularItemsStatus === DataStatus.FETCHING
    ) {
      // Data already fetched and data valid, or fetching. Do not fetch
      return;
    }

    // Data not fetched or invalid, let's fetch
    commit(SET_POPULAR_ITEMS_STATUS, DataStatus.FETCHING);
    commit(SET_POPULAR_ITEMS_LAST_FETCHED, Date.now());

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/introspection/popular_items`, RequestMethod.GET)
      .then(response => {
        const popularItems: PopularItems = response.data;
        commit(SET_POPULAR_ITEMS, popularItems);
        commit(SET_POPULAR_ITEMS_STATUS, DataStatus.FETCHED);
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PREFIX_POPULAR_ITEMS_ERROR} (${error})`);

        // Set empty values on error so loading doesn't continue infinitely
        commit(SET_POPULAR_ITEMS, {
          time_slice: 0,
          most_hit_keys: null,
          most_missed_keys: null,
          most_mutated_keys: null,
          popular_prefixes: null,
        });

        commit(SET_POPULAR_ITEMS_STATUS, DataStatus.FAILED);
        commit(SET_POPULAR_ITEMS_LAST_FETCHED, 0);
      });
  },
  fetchPrefixes({ commit, dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/introspection/prefixes`, RequestMethod.GET)
      .then(response => {
        const prefixes: Prefix[] = response.data;
        prefixes.sort(function(a: Prefix, b: Prefix) {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;
          return 0;
        });
        commit(SET_PREFIXES, prefixes);
      })
      .catch(error => {
        commit(SET_PREFIXES, []);
        setNotificationError(dispatch, `${Msg.PREFIXES_ERROR} (${error})`);
      });
  },
  fetchPrefixStatsByTimeRange({ dispatch }, prefixID: number) {
    for (const timeRange of TIME_RANGES) {
      dispatch("fetchPrefixStats", { timeRange, prefixID });
    }
  },
  fetchPrefixStats({ commit, dispatch, state, rootState }, payload: { timeRange: TimeRange; prefixID: number }) {
    const { timeRange, prefixID } = payload;

    const valid = !!(
      state.prefixStatsLastFetched[prefixID] &&
      state.prefixStatsLastFetched[prefixID][timeRange] &&
      state.prefixStatsLastFetched[prefixID][timeRange] + PREFIX_STATS_LIFETIME - LIFETIME_MARGIN >= Date.now()
    );

    if (
      (state.prefixStatsStatus[prefixID] &&
        state.prefixStatsStatus[prefixID][timeRange] === DataStatus.FETCHED &&
        valid) ||
      (state.prefixStatsStatus[prefixID] && state.prefixStatsStatus[prefixID][timeRange] === DataStatus.FETCHING)
    ) {
      // Data already fetched and data valid, or fetching. Do not fetch
      return;
    }

    // Data not fetched or invalid, let's fetch
    commit(SET_PREFIX_STATS_STATUS, {
      prefixID: prefixID,
      timeRange: timeRange,
      status: DataStatus.FETCHING,
    });
    commit(SET_PREFIX_STATS_LAST_FETCHED, {
      prefixID: prefixID,
      timeRange: timeRange,
      timestamp: Date.now(),
    });

    const { startTime, endTime } = getTimeRange(rootState.timeRange, timeRange);

    const params = {
      startTime,
      endTime,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/introspection/prefixes/${prefixID}/stats`, RequestMethod.GET, params)
      .then(async response => {
        if (response.data === null) {
          dispatch("setEmptyPrefixStatSeries", {
            timeRange: timeRange,
            status: DataStatus.FETCHED,
            prefixID: prefixID,
          });
          return;
        }

        let seriesArray: Series[] = response.data;

        // prefix stats should always be an array of one series,
        // unlike processed stats where there is a series per server,
        // map anyway to be more robust
        seriesArray = seriesArray.map(series => {
          trimNullPoints(series.values);

          const appendedSeries = appendCalculatedMetricsToSeries(series);

          // prepend columns names only after appending calculated metrics
          const prependedColumns = prependColumnNames(appendedSeries.columns, "prefix_");

          return {
            columns: prependedColumns,
            name: series.name,
            values: appendedSeries.values,
          };
        });

        // Combine prefix stats and compound stats into a single series,
        // so prefix stats charts can be created comparing compound stats data

        // Fetch compound stats if not already cached
        await dispatch("fetchCompoundStats", timeRange);

        // Note: calculate metrics requiring both prefix and compound stats in
        // combinePrefixAndCompoundStats() in utils/debug-mode
        const allCompoundStats = state.compoundStats[timeRange]["allStats"];

        seriesArray = seriesArray.map((series, i) => {
          series = combinePrefixAndCompoundStats(series, allCompoundStats[i]);
          return series;
        });

        // commit all stats to have a convenient way of retrieving data for
        // things like last stats
        const allPayload: PrefixSeriesPayload = {
          prefixID,
          timeRange,
          chartID: "allStats",
          seriesArray: seriesArray,
        };

        commit(SET_PREFIX_STATS, allPayload);

        // commit stats per chart
        for (const chartID of getChartIDsFor(StatType.PREFIX)) {
          const cPayload: PrefixSeriesPayload = {
            prefixID,
            timeRange,
            chartID,
            seriesArray: generateChartSeries(seriesArray, chartID),
          };
          commit(SET_PREFIX_STATS, cPayload);
        }

        commit(SET_PREFIX_STATS_STATUS, {
          prefixID: prefixID,
          timeRange: timeRange,
          status: DataStatus.FETCHED,
        });
      })
      .catch(error => {
        setNotificationError(
          dispatch,
          `${Msg.PREFIX_STATS_ERROR} for prefix ID ${prefixID} for ${timeRange} (${error})`,
        );

        dispatch("setEmptyPrefixStatSeries", {
          timeRange: timeRange,
          status: DataStatus.FAILED,
          prefixID: prefixID,
        });

        commit(SET_PREFIX_STATS_LAST_FETCHED, {
          prefixID: prefixID,
          timeRange: timeRange,
          timestamp: 0,
        });
      });
  },
  fetchPrefixPopularItems({ commit, dispatch, state, rootState }, prefixID: number) {
    const valid = !!(
      state.prefixPopularItemsLastFetched[prefixID] &&
      state.prefixPopularItemsLastFetched[prefixID] + PREFIX_POPULAR_ITEMS_LIFETIME - LIFETIME_MARGIN >= Date.now()
    );

    if (
      (state.prefixPopularItemsStatus[prefixID] === DataStatus.FETCHED && valid) ||
      state.prefixPopularItemsStatus[prefixID] === DataStatus.FETCHING
    ) {
      // Data already fetched and data valid, or fetching. Do not fetch
      return;
    }

    // Data not fetched or invalid, let's fetch
    commit(SET_PREFIX_POPULAR_ITEMS_STATUS, {
      prefixID: prefixID,
      status: DataStatus.FETCHING,
    });
    commit(SET_PREFIX_POPULAR_ITEMS_LAST_FETCHED, {
      prefixID: prefixID,
      timestamp: Date.now(),
    });

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/introspection/prefixes/${prefixID}/popular_items`, RequestMethod.GET)
      .then(response => {
        const popularItems: PopularItems = response.data;
        commit(SET_PREFIX_POPULAR_ITEMS, {
          prefixID: prefixID,
          popularItems: popularItems,
        });

        commit(SET_PREFIX_POPULAR_ITEMS_STATUS, {
          prefixID: prefixID,
          status: DataStatus.FETCHED,
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PREFIX_POPULAR_ITEMS_ERROR} for prefix ID ${prefixID} (${error})`);

        commit(SET_PREFIX_POPULAR_ITEMS_STATUS, {
          prefixID: prefixID,
          status: DataStatus.FAILED,
        });
        commit(SET_PREFIX_POPULAR_ITEMS_LAST_FETCHED, {
          prefixID: prefixID,
          timestamp: 0,
        });
      });
  },
  addPrefix({ dispatch, rootState }, prefix: Prefix) {
    if (isAdminUsingProd()) return;
    const data = {
      name: prefix.name,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/introspection/prefixes`, RequestMethod.POST, {}, data)
      .then(() => {
        dispatch("fetchPrefixes");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PREFIX_ADD_ERROR} (${error})`);
      });
  },
  removePrefix({ dispatch, rootState }, prefix: Prefix) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/introspection/prefixes/${prefix.id}`, RequestMethod.DELETE)
      .then(() => {
        dispatch("fetchPrefixes");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PREFIX_REMOVE_ERROR} (${error})`);
      });
  },
  fetchKeyStats({ commit, dispatch, rootState }, key: string) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/introspection/key_stats?key=${key}`, RequestMethod.GET)
      .then(response => {
        const stats: KeyStatsPoint[] = response.data;
        let statsSeries: SeriesPoint[] = []; // default in case `stats` is empty

        if (stats.length > 0) {
          // TODO abstract to helper
          // sort chronologically
          stats.sort(function(a: any, b: any) {
            if (a.timestamp < b.timestamp) return -1;
            if (a.timestamp > b.timestamp) return 1;
            return 0;
          });

          // calculate hit rate
          for (const s of stats) {
            const requests = s.hit_count + s.miss_count;

            let hitRate = 0;
            if (requests > 0) {
              hitRate = s.hit_count / requests;
            }
            s.hit_rate = hitRate;

            // convert per 10 mins to per second
            s.hit_count_per_sec = s.hit_count / 600;
            s.miss_count_per_sec = s.miss_count / 600;
            s.mutation_count_per_sec = s.mutation_count / 600;
          }

          // create stats series
          statsSeries = stats.map(s => {
            return [
              s.timestamp,
              s.name,
              s.size,
              s.hit_count,
              s.miss_count,
              s.mutation_count,
              s.hit_rate,
              s.hit_count_per_sec,
              s.miss_count_per_sec,
              s.mutation_count_per_sec,
            ];
          });
        }

        commit(SET_KEY_STATS_SERIES, {
          name: key,
          series: statsSeries,
        });
        commit(SET_KEY_STATS, {
          name: key,
          stats: stats,
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.KEY_STATS_ERROR} (${error})`);
      });
  },
  setShowAddPrefix({ commit }, show: boolean) {
    commit(SET_SHOW_ADD_PREFIX, show);
  },
  setEmptyCompoundStatSeries({ commit }, payload: { timeRange: TimeRange; status: DataStatus }) {
    const { timeRange, status } = payload;
    // set empty values for series' data. To show data unavailable, preventing indefinite loading
    const chartIDs = getChartIDsFor(StatType.COMPOUND);
    chartIDs.unshift("allStats");

    for (const chartID of chartIDs) {
      const payload: SeriesPayload = {
        timeRange: timeRange,
        chartID: chartID,
        seriesArray: emptySeriesArray(),
      };
      commit(SET_COMPOUND_STATS, payload);
    }

    commit(SET_COMPOUND_STATS_STATUS, { timeRange: timeRange, status: status });
  },
  setEmptyPrefixStatSeries(
    { commit },
    payload: {
      timeRange: TimeRange;
      status: DataStatus;
      prefixID: number;
    },
  ) {
    const { timeRange, status, prefixID } = payload;
    // set empty values for series' data. To show data unavailable, preventing indefinite loading
    const chartIDs = getChartIDsFor(StatType.PREFIX);
    chartIDs.unshift("allStats");

    for (const chartID of chartIDs) {
      const payload: PrefixSeriesPayload = {
        prefixID: prefixID,
        timeRange: timeRange,
        chartID,
        seriesArray: emptySeriesArray(),
      };
      commit(SET_PREFIX_STATS, payload);
    }

    commit(SET_PREFIX_STATS_STATUS, { prefixID: prefixID, timeRange: timeRange, status: status });
  },
};

const getters: GetterTree<DebugModeState, RootState> = {
  slabStatsSeries: (state, getters, rootState) => (chartID: string): false | Series => {
    const seriesArray = state.processedSlabStats[rootState.timeRange][chartID];

    // Make sure data has been returned by the API before trying to render chart
    if (!seriesArray || (seriesArray && seriesArray.length < 1)) return false;

    return seriesArray[0];
  },
  compoundStatsSeries: (state, getters, rootState) => (chartID: string): false | Series => {
    const seriesArray = state.compoundStats[rootState.timeRange][chartID];

    // Make sure data has been returned by the API before trying to render chart
    if (!seriesArray || (seriesArray && seriesArray.length < 1)) return false;

    return seriesArray[0];
  },
  prefixStatsSeries: (state, getters, rootState) => (
    chartID: string,
    prefixID?: number,
    timeRange?: TimeRange,
  ): Series | false => {
    if (!prefixID) {
      prefixID = rootState.activePrefixID;
    }

    if (!timeRange) {
      timeRange = rootState.timeRange;
    }

    if (
      state.prefixStats[prefixID] &&
      state.prefixStats[prefixID][timeRange] &&
      state.prefixStats[prefixID][timeRange][chartID] &&
      state.prefixStats[prefixID][timeRange][chartID][0]
    ) {
      return state.prefixStats[prefixID][timeRange][chartID][0];
    }
    return false;
  },
  prefixStatsSeriesColumns: (state, getters) => (prefixID: number): false | string[] => {
    const series = getters.prefixStatsSeries("allStats", prefixID, TimeRange.LAST_DAY);
    if (series) {
      return series.columns;
    }
    return false;
  },
  prefixLastStats: (state, getters) => (prefixID: number): SeriesPoint | null | false => {
    const series = getters.prefixStatsSeries("allStats", prefixID, TimeRange.LAST_DAY);
    if (!series) {
      return false; // Loading
    }

    const points = series.values;

    if (points.length < 1) {
      return null; // no data/unavailable
    }

    return points[points.length - 1];
  },
  allPrefixLastStatsFetched: state => {
    // prefixes default to null, and are unfetched
    if (!state.prefixes) return false;

    // if at least one prefix is not fetched
    for (const prefix of state.prefixes) {
      if (
        !state.prefixStatsStatus[prefix.id] ||
        state.prefixStatsStatus[prefix.id][TimeRange.LAST_DAY] !== DataStatus.FETCHED
      ) {
        return false;
      }
    }

    return true;
  },
  popularPrefixes: state => {
    return state.popularItems ? state.popularItems.popular_prefixes : undefined;
  },
  prefixIssues: (state, getters) => (prefixID: number) => {
    const issues: {
      activity?: Issue;
    } = {};

    const columns = getters.prefixStatsSeriesColumns(prefixID);
    const lastStats = getters.prefixLastStats(prefixID);

    if (columns && lastStats) {
      // Prefix per second rates
      const hI = getValuesIdxByMetric(columns, "prefix_total_hits_rate");
      const hits = lastStats[hI];

      const miI = getValuesIdxByMetric(columns, "prefix_total_misses_rate");
      const misses = lastStats[miI];

      const muI = getValuesIdxByMetric(columns, "prefix_total_mutations_rate");
      const mutations = lastStats[muI];

      // The following indicate potential issues:

      // Prefix activity issues
      if (hits === 0) {
        let activityMsg = "";

        if (misses === 0 && mutations > 0) {
          activityMsg = "You appear to store data that is not fetched.";
        } else if (misses > 0 && mutations === 0) {
          activityMsg = "You appear to fetch and miss all values but make no attempt at storing data.";
        } else if (misses > 0 && mutations > 0) {
          activityMsg = "You appear to fetch and miss all values. Are you storing the correct data?";
        }

        if (activityMsg) {
          issues.activity = {
            type: NotificationType.WARNING,
            message: activityMsg,
            icon: "exclamation-circle",
          };
        }
      }
    }

    return issues;
  },
  activePrefix: (state, getters, rootState) => {
    if (state.prefixes) {
      const activePrefix = state.prefixes.find(prefix => prefix.id === rootState.activePrefixID);
      if (activePrefix) {
        return activePrefix;
      }
    }
    return { id: 0, name: "" } as Prefix;
  },
};

export const debugMode: Module<DebugModeState, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
